// src/lib/estimatorApi.ts

export interface EstimationResult {
  estimated_price: number;
  price_range: { min: number; max: number };
  market_analysis: string;
  similar_listings: Array<{
    title: string;
    price: number;
    source: string;
  }>;
  factors: string[];
  recommendation: string;
}

export interface EstimatorFormData {
  brand: string;
  model: string;
  year: string;
  mileage: string;
  fuelType: string;
  vehicleType: string;
  transmission: string;
  horsepower: string;
}

export const estimatorApi = {
  async getEstimation(formData: EstimatorFormData): Promise<EstimationResult> {
    console.log("Requesting estimation via AI proxy...");
    
    // UX: Add artificial start time to ensure loading doesn't flash too quickly
    const startTime = Date.now();

    try {
      const response = await fetch('/api/estimate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ formData }),
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const aiResult = await response.json();

      // VALIDATION: Ensure AI didn't hallucinate a weird number
      // If price is < 5000 TND (impossible for a car), assume error and use fallback
      if (!aiResult.estimated_price || isNaN(aiResult.estimated_price) || aiResult.estimated_price < 5000) {
        throw new Error('Invalid price data received from AI');
      }

      // UX: Ensure at least 1.5 seconds of "thinking" time so user trusts the result
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < 1500) {
        await new Promise(resolve => setTimeout(resolve, 1500 - elapsedTime));
      }

      return aiResult;

    } catch (error: any) {
      console.warn('AI Estimation failed/rejected, switching to Smart Fallback:', error);
      
      // UX: Ensure delay even on error for consistency
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < 1500) {
        await new Promise(resolve => setTimeout(resolve, 1500 - elapsedTime));
      }

      return this.getFallbackEstimation(formData);
    }
  },

  // "Smart Fallback" - Runs locally if AI fails
  getFallbackEstimation(data: EstimatorFormData): EstimationResult {
    const currentYear = new Date().getFullYear();
    const vehicleYear = parseInt(data.year);
    const age = Math.max(0, currentYear - vehicleYear);
    const mileage = parseInt(data.mileage) || 0;
    
    // 1. Base Price Logic (Tunisian Market Context - 2024/2025 Estimates)
    // Approximate "New" prices to depreciate from
    let baseNewPrice = 75000; // Generic average

    const luxuryBrands = ['Mercedes-Benz', 'BMW', 'Audi', 'Porsche', 'Land Rover', 'Volvo', 'Jeep'];
    const premiumBrands = ['Volkswagen', 'Toyota', 'Honda', 'Mazda', 'Ford', 'Peugeot'];
    const economyBrands = ['Fiat', 'Renault', 'Citroën', 'Dacia', 'Suzuki', 'Kia', 'Hyundai', 'Chery', 'MG'];

    if (luxuryBrands.includes(data.brand)) baseNewPrice = 200000;
    else if (premiumBrands.includes(data.brand)) baseNewPrice = 110000;
    else if (economyBrands.includes(data.brand)) baseNewPrice = 60000;

    // Adjust for transmission/fuel
    if (data.transmission === 'Automatique') baseNewPrice *= 1.15;
    if (data.fuelType === 'Hybride') baseNewPrice *= 1.20;

    // 2. Depreciation Logic (Tunisia Specific - Low Depreciation)
    let estimated = baseNewPrice;
    
    // Year 1 drop (Registration/Taxes): ~15%
    if (age > 0) estimated *= 0.85;
    
    // Subsequent years: ~6-8% drop per year
    const yearlyDepreciation = luxuryBrands.includes(data.brand) ? 0.92 : 0.94; 
    for (let i = 1; i < age; i++) {
      estimated *= yearlyDepreciation;
    }

    // 3. Mileage Adjustment
    // Standard average in Tunisia is ~20,000 km/year
    const standardMileage = age * 20000;
    const mileageDiff = standardMileage - mileage;
    
    // Logic: 2.5% value shift per 10k km difference
    const mileageFactor = (mileageDiff / 10000) * 0.025; 
    const cappedMileageFactor = Math.max(-0.25, Math.min(0.15, mileageFactor));
    
    estimated = estimated * (1 + cappedMileageFactor);

    // 4. Brand Popularity Bonus (Liquidity Premium)
    const liquidBrands = ['Kia', 'Hyundai', 'Volkswagen', 'Peugeot', 'Renault', 'Toyota'];
    if (liquidBrands.includes(data.brand)) {
        estimated *= 1.05; // +5% market liquidity bonus
    }

    // Final Rounding to nearest 100 TND
    estimated = Math.round(estimated / 100) * 100;
    
    // Floor price safety (Cars rarely drop below 15k unless very old)
    if (estimated < 12000) estimated = 12000;

    return {
      estimated_price: estimated,
      price_range: { 
        min: Math.round(estimated * 0.93), 
        max: Math.round(estimated * 1.07) 
      },
      market_analysis: `Estimation basée sur les tendances historiques du marché tunisien pour une ${data.brand} ${data.model}. Ce modèle bénéficie d'une ${liquidBrands.includes(data.brand) ? "forte" : "moyenne"} demande sur le marché de l'occasion.`,
      similar_listings: [
        { 
          title: `${data.brand} ${data.model} (${data.year})`, 
          price: Math.round(estimated * 1.02), 
          source: "Annonce Particulier (Simulé)" 
        },
        { 
          title: `${data.brand} ${data.model} Similaire`, 
          price: Math.round(estimated * 0.96), 
          source: "Revendeur Local (Simulé)" 
        }
      ],
      factors: [
        mileage < standardMileage ? "Faible kilométrage (Positif)" : "Kilométrage standard/élevé",
        liquidBrands.includes(data.brand) ? "Marque à forte liquidité" : "Dépréciation standard",
        age <= 5 ? "Modèle récent" : "Âge avancé"
      ],
      recommendation: "Cette estimation est basée sur notre algorithme de secours. Pour une précision maximale, veuillez réessayer dans quelques instants."
    };
  }
};